import get from 'lodash/get';
import type { Dispatch, ForwardedRef, SetStateAction } from 'react';
import { forwardRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import type { GlobalError } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import { GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY } from '@/lib/const';
import { paths } from '@/lib/paths';
import { generateDataTestId } from '@/lib/tests';

export interface ReCaptchaProps {
  languageCode?: string;
  name: string;
  onChange: Dispatch<SetStateAction<boolean>>;
  validation?: boolean;
}

export const ReCaptcha = forwardRef(
  (
    { languageCode, name, onChange, validation = true }: ReCaptchaProps,
    ref?: ForwardedRef<ReCAPTCHA>,
  ) => {
    const { control, formState } = useFormContext();

    const error = get(formState.errors, name) as GlobalError;

    const handleReCaptchaChange = async (token: string | null) => {
      onChange(true);
      if (validation) {
        const googleValidation = await fetch(
          paths.api.tokenVerification.asPath(),
          {
            method: 'POST',
            body: token,
          },
        );
        onChange(false);
        return googleValidation.ok;
      }
      onChange(false);
      return true;
    };

    return (
      <Flex
        alignItems='center'
        data-test-id='ReCaptchaFlex'
        flexDirection='column'
        justifyContent='center'
        {...generateDataTestId('input', 'captcha')}
      >
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange } }) => (
            <ReCAPTCHA
              ref={ref}
              data-test-id='ReCaptchaController'
              hl={languageCode ?? 'EN'}
              onChange={async (value) => {
                const isReCaptchaVerified = await handleReCaptchaChange(value);
                onChange(isReCaptchaVerified);
              }}
              onExpired={() => onChange(false)}
              sitekey={GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY}
            />
          )}
        />
        {error && (
          <Typography color='error' data-test-id='ReCaptchaError' scale='p3'>
            {error.message}
          </Typography>
        )}
      </Flex>
    );
  },
);

ReCaptcha.displayName = 'ReCaptcha';
